// Sub-Step 3.3 — OidcAuthProvider für public/-Stack.
//
// Haelt react-oidc-context aus dem Vike-SSR-Tree heraus
// (localStorage/sessionStorage-Zugriff crashed Server-Render);
// der Client rendert den Provider von Render 1 an (s. Doppel-Boot-Fix).
//
// Verwendet von public/pages/+Layout.tsx (Root-Wrapper). Alle Pages
// laufen unter diesem Provider; Components können via `useAuth()` aus
// react-oidc-context (oder usePublicAuth-Wrapper) den Auth-State lesen.
//
// 2026-05-28: AutoSilentSso-Component entfernt (signinSilent() bei Mount).
// Bene-iPhone-Befund: iOS Safari zeigte "Erweiterte Datenschutzmaßnahmen"-
// Banner beim Reload. Trigger war der Silent-SSO-iframe zu
// auth.scrolly.com (cross-subdomain). Erst-Visit: keine Cookies bei
// auth → kein ITP-Block. Reload: Cookies vom Initial-iframe da →
// Safari blockt Cross-Site-Cookies → "Privacy Report"-Banner.
//
// 2026-05-28 (later): SsoHandoffReceiver v1 — speicherte den per
// postMessage empfangenen userString direkt in sessionStorage + reload.
// Revertiert (4ff204d): iframe-Pfad loopte (onLoad→send→reload→onLoad).
//
// 2026-06-10 (B5, Launch-Batch-2): SsoHandoffReceiver v2 — HINT-Modell.
// Das Portal signalisiert nur noch „Keycloak-Session existiert"; der
// Receiver macht daraufhin EINMAL pro Tab einen top-level
// signinRedirect (prompt=none) mit dem EIGENEN Client scrolly-public.
// Kein fremdes Token mehr im Storage (5-min-Lifespan + Client-Mismatch
// beim Renew), kein reload-on-message (= der Loop-Motor von v1).
// Guards: Origin-Allowlist, iframe-Detection, once-Flag (sessionStorage,
// per Tab), nur-wenn-anonym. Sender: portal/src/lib/sso-handoff.ts.
// Direkt-Links (Bookmark, Suchmaschine) bleiben anonym.
// Begründung + Entscheidungslogik: src/lib/auth/sso-hint.ts.

import { merkePortalHerkunft } from "../../lib/herkunft-portal";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { getOidcConfig } from "../../lib/auth/oidc-config";
import {
  SSO_HANDOFF_MESSAGE_TYPE,
  SSO_ACK_MESSAGE_TYPE,
  SSO_ATTEMPTED_KEY,
  SSO_RETURN_TO_KEY,
  SILENT_SSO_FAIL_ERRORS,
  decideSsoHint,
  hatAuthParams,
  hatSsoQuery,
  ohneSsoQuery,
  revealPage,
  silentRedirectUri,
  stripAuthParams,
} from "../../lib/auth/sso-hint";

interface Props {
  children: ReactNode;
}

/**
 * Allowlist für origin-Validierung im SsoHandoffReceiver.
 * Nur diese Origins dürfen via postMessage Auth-State setzen.
 * Bei neuen Konsumenten-Domains hier ergänzen.
 *
 * Hardening 2026-05-28: localhost-Origins nur im Dev-Build aufnehmen.
 * In Production-Builds (import.meta.env.DEV === false) wird der
 * Conditional-Spread leer — Vite tree-shaked die Strings raus, so dass
 * der Prod-Bundle keine localhost-Origins akzeptiert.
 *
 * Hintergrund: eine maliziöse Seite auf localhost im selben Browser-
 * Session als der Prod-Viewer könnte sonst einen gefälschten Auth-
 * Handoff posten und sich als authentifizierter User ausgeben.
 * Attack-Surface ist hoch (User muss in einer Session malicious-
 * localhost UND prod-viewer offen haben), aber Defense-in-Depth.
 */
const ALLOWED_SSO_ORIGINS: string[] = [
  ...(import.meta.env.VITE_ALLOWED_SSO_ORIGINS || "https://portal.scrolly.com,https://my.scrolly.com")
    .split(",")
    .map((s: string) => s.trim())
    .filter(Boolean),
  // Dev: portal läuft typisch auf 5173, public auf 5176.
  // In Prod-Builds wird das Array via Vite-Tree-Shake leer.
  ...(import.meta.env.DEV
    ? ["http://localhost:5173", "http://localhost:5176"]
    : []),
];

/**
 * Empfängt den SSO-HINT von portal-Tabs (v2 — kein Token-Transfer).
 *
 * Message-Format (sync mit portal/src/lib/sso-handoff.ts):
 *   { type: "scrolly-sso-handoff", payload: { mode: "hint" } }
 *
 * Antwortet mit Ack damit der Sender sein Retry-Polling stoppt:
 *   { type: "scrolly-sso-ack" }
 *
 * Bei "redirect"-Entscheidung (s. decideSsoHint): EINMAL pro Tab ein
 * top-level signinRedirect(prompt=none) mit scrolly-public. Existiert
 * die Keycloak-SSO-Session (Portal-Login), kommt der User ohne Form
 * eingeloggt zurück (return_to via url_state); existiert sie nicht,
 * fängt oidc-callback den login_required-Fehler ab und kehrt still
 * anonym zur Scrolly-URL zurück (SSO_RETURN_TO_KEY-Fallback).
 */
function SsoHandoffReceiver() {
  const auth = useAuth();
  const [pendingHint, setPendingHint] = useState(false);
  const actedRef = useRef(false);

  // Listener: Origin validieren, Ack senden, Hint vormerken.
  useEffect(() => {
    const handler = (ev: MessageEvent) => {
      if (!ALLOWED_SSO_ORIGINS.includes(ev.origin)) return; // ignore: kein Ack an Fremde
      if ((ev.data as { type?: string } | undefined)?.type !== SSO_HANDOFF_MESSAGE_TYPE) {
        return;
      }

      // Ack IMMER an erlaubte Sender — auch wenn kein Redirect folgt
      // (eingeloggt / attempted / iframe). Stoppt das Polling drüben.
      if (ev.source && "postMessage" in ev.source) {
        (ev.source as Window).postMessage(
          { type: SSO_ACK_MESSAGE_TYPE },
          { targetOrigin: ev.origin },
        );
      }
      setPendingHint(true);
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // URL-Hint (Jan 15.09.2026, „ein Viewer"): `?sso=1` vom Portal im selben
  // Tab. Parameter sofort aus der Adresse nehmen, damit return_to, Teilen
  // und Reload ohne ihn laufen; dann wie ein postMessage-Hint behandeln.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hatSsoQuery(window.location.search)) return;
    try {
      window.history.replaceState(window.history.state, "", ohneSsoQuery(window.location.href));
    } catch {
      /* replaceState kann in exotischen Kontexten werfen — Hint trotzdem verarbeiten */
    }
    setPendingHint(true);
  }, []);

  // Rueckkehr vom stillen Sprung (Weg 2, 2026-09-18): react-oidc-context hat
  // code/state bzw. error/state auf DIESER Seite verarbeitet. Erfolg: Adresse
  // ist schon sauber (onSigninCallback). Silent-Fail (login_required): die
  // Fehler-Parameter stehen noch in der Adresse → still entfernen, anonym
  // bleiben. In beiden Faellen die Seite wieder aufdecken.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (auth.isLoading || auth.activeNavigator) return;
    if (auth.error && isSilentSsoFail(auth.error) && hatAuthParams(window.location.search)) {
      try {
        window.history.replaceState(window.history.state, "", stripAuthParams(window.location.href));
      } catch {
        /* noop */
      }
    }
    if (auth.isAuthenticated || auth.error || !pendingHint) revealPage();
  }, [auth.isLoading, auth.activeNavigator, auth.isAuthenticated, auth.error, pendingHint]);

  // Verarbeitung: erst entscheiden, wenn der Auth-State geladen ist.
  useEffect(() => {
    if (!pendingHint || actedRef.current) return;
    if (auth.isLoading || auth.activeNavigator) return;

    const decision = decideSsoHint({
      originAllowed: true, // bereits im Listener geprüft
      isTopWindow: window.self === window.top,
      attempted: safeGetSessionItem(SSO_ATTEMPTED_KEY) === "1",
      isAuthenticated: auth.isAuthenticated,
    });

    actedRef.current = true;
    if (decision !== "redirect") {
      revealPage();
      return;
    }

    try {
      // Flags VOR dem Redirect — once-Semantik hält auch bei Fehlschlag
      // oder Browser-Back. sessionStorage ist per-Tab und überlebt den
      // Redirect-Roundtrip (view → auth → view).
      sessionStorage.setItem(SSO_ATTEMPTED_KEY, "1");
      sessionStorage.setItem(SSO_RETURN_TO_KEY, window.location.href);
    } catch {
      // Storage gesperrt (Private-Mode-Edge) → lieber anonym bleiben
      // als ohne once-Guard redirecten.
      revealPage();
      return;
    }

    // Weg 2 (2026-09-18): Rueckkehr direkt auf die Scrolly-Adresse statt
    // /oidc-callback — eine Seite weniger, kein Text, kein Doppelbild.
    // Der Keycloak-Client scrolly-public erlaubt dafuer `<viewer>/*`.
    const zurueck = silentRedirectUri(window.location.href);
    // Guertel zum Hosentraeger: Herkunft auch hier merken, falls der Sprung
    // startet, bevor ViewerChrome geladen ist (s. lib/herkunft-portal.ts).
    merkePortalHerkunft();
    void auth.signinRedirect({
      prompt: "none",
      redirect_uri: zurueck,
      url_state: JSON.stringify({ return_to: zurueck }),
    });
  }, [pendingHint, auth, auth.isLoading, auth.activeNavigator, auth.isAuthenticated]);

  return null;
}

/** login_required & Co. sind beim prompt=none-Sprung erwartet (s. oidc-callback). */
function isSilentSsoFail(error: unknown): boolean {
  const e = error as { error?: unknown; message?: unknown } | null | undefined;
  if (typeof e?.error === "string" && SILENT_SSO_FAIL_ERRORS.has(e.error)) return true;
  return typeof e?.message === "string" && SILENT_SSO_FAIL_ERRORS.has(e.message);
}

/** sessionStorage.getItem ohne Throw (Private-Mode/Storage-Block-Edge). */
function safeGetSessionItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Root-Wrapper-Component für alle Vike-Pages.
 *
 * Verwendung in public/pages/+Layout.tsx (oder einer äquivalenten
 * Root-Komponente). SSR rendert die Children ohne Provider (kein
 * Auth-Hook serverseitig); der Client rendert AuthProvider ab dem
 * ersten Render — kein Post-Mount-Flip, kein Seiten-Remount.
 */
export default function OidcAuthProvider({ children }: Props) {
  // DOPPEL-BOOT-FIX (Jans "Blitzer wie ein schneller Reload", 02.09.2026):
  // Vorher <ClientOnly fallback={children}> — der Post-Mount-Flip wickelte
  // die Children nachtraeglich in AuthProviderInner ein. Fuer React ist das
  // ein Element-Typwechsel an derselben Baumposition → die KOMPLETTE Seite
  // (inkl. Renderer, Layer-Baum, Fonts) wurde unmounted und neu gemountet:
  // jeder Seitenaufruf bootete doppelt. Der Provider erzeugt aber kein DOM —
  // die Hydration matcht nur Host-Elemente, ein Context-Provider dazwischen
  // stoert sie nicht. Also: Server ohne Provider (UserManager braucht
  // window), Client von RENDER 1 an mit Provider — Struktur kippt nie mehr.
  if (typeof window === "undefined") {
    return <>{children}</>;
  }
  return <AuthProviderInner>{children}</AuthProviderInner>;
}

/**
 * Inner-Component: darf nur client-seitig gerendert werden, weil
 * getOidcConfig() window.location.origin liest (server-side undefined).
 */
function AuthProviderInner({ children }: Props) {
  const config = getOidcConfig();
  return (
    <AuthProvider {...config}>
      <SsoHandoffReceiver />
      {children}
    </AuthProvider>
  );
}
